import { formatCurrency } from 'utils';
import {
  LenderRetailActivityCountryRowViewModel,
  LenderRetailActivityDealerRowViewModel,
  LenderRetailActivityDeclineReasonRowViewModel,
  LenderRetailActivityLenderRowViewModel,
  LenderRetailActivityStateRowViewModel,
} from '../api/types';
import {
  CreditDecisionDatum,
  DealerActivityColumnConfig,
  DealerActivityGroup,
  DealerActivityRow,
  DeclineReasonDatum,
  FinancedDistributionDatum,
  LenderActivityFilterFormValues,
  LenderAppsColumnConfig,
  LenderAppsGroup,
  LenderAppsRow,
  LenderRetailActivityReportInput,
} from '../types';

export const mapFiltersToReportInput = (values: LenderActivityFilterFormValues): LenderRetailActivityReportInput => ({
  filters: {
    created: {
      from: values.from.format('YYYY-MM-DD'),
      to: values.to.format('YYYY-MM-DD'),
    },
  },
});

export const LENDER_APPS_TOTAL_ID = 'lender-apps-total';
export const DEALER_ACTIVITY_TOTAL_ID = 'dealer-activity-total';

const COUNTRY_LABELS: Record<string, string> = { CA: 'Canada' };
const COUNTRY_ORDER: Record<string, number> = { US: 0, CA: 1 };

export const normalizeCountry = (country: string): string => country.trim().toUpperCase();

export const getCountryLabel = (country: string): string => COUNTRY_LABELS[normalizeCountry(country)] ?? country;

export const getCountryRank = (country: string): number => COUNTRY_ORDER[normalizeCountry(country)] ?? 99;

export const formatCount = (value: number): string => value.toLocaleString();

export const formatAmount = (value: number): string => formatCurrency(value, true) || '$0';

// Table amounts are shown as plain numbers; the '($)' is carried by the column header.
export const formatAmountValue = (value: number): string => Math.round(value).toLocaleString();

export const formatRate = (value: number): string => `${value.toFixed(2)}%`;

export const formatRateWhole = (value: number): string => `${Math.round(value)}%`;

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCompactAmount = (value: number): string => compactCurrencyFormatter.format(value);

export const getRateOfTotal = (value: number, total: number): number => (total ? (value / total) * 100 : 0);

export const mapLenderAppsRows = (rows: LenderRetailActivityLenderRowViewModel[]): LenderAppsRow[] =>
  rows.map(row => ({
    id: `${row.country}-${row.lenderProfileId ?? row.lenderName}`,
    country: row.country,
    lenderName: row.lenderName,
    approved: row.approved,
    declined: row.declined,
    pending: row.pending,
    approvalRate: row.approvalRate,
    approvedAmount: row.approvedAmount,
    funded: row.funded,
    fundedAmount: row.fundedAmount,
    requestedAmount: row.requestedAmount,
  }));

export const groupLenderRowsByCountry = (rows: LenderRetailActivityLenderRowViewModel[]): LenderAppsGroup[] => {
  const groups = new Map<string, LenderAppsRow[]>();

  mapLenderAppsRows(rows).forEach(row => {
    const list = groups.get(row.country) ?? [];
    list.push(row);
    groups.set(row.country, list);
  });

  return Array.from(groups.entries())
    .map(([country, countryRows]) => ({ country, rows: countryRows }))
    .sort((a, b) => getCountryRank(a.country) - getCountryRank(b.country));
};

export const getLenderAppsTotalRow = (rows: LenderRetailActivityLenderRowViewModel[]): LenderAppsRow | null => {
  if (!rows.length) return null;

  const totals = rows.reduce(
    (acc, row) => ({
      approved: acc.approved + row.approved,
      declined: acc.declined + row.declined,
      pending: acc.pending + row.pending,
      approvedAmount: acc.approvedAmount + row.approvedAmount,
      funded: acc.funded + row.funded,
      fundedAmount: acc.fundedAmount + row.fundedAmount,
      requestedAmount: acc.requestedAmount + row.requestedAmount,
    }),
    {
      approved: 0,
      declined: 0,
      pending: 0,
      approvedAmount: 0,
      funded: 0,
      fundedAmount: 0,
      requestedAmount: 0,
    }
  );

  return {
    id: LENDER_APPS_TOTAL_ID,
    country: '',
    lenderName: 'Total',
    approved: totals.approved,
    declined: totals.declined,
    pending: totals.pending,
    approvalRate: getRateOfTotal(totals.approved, totals.approved + totals.declined + totals.pending),
    approvedAmount: totals.approvedAmount,
    funded: totals.funded,
    fundedAmount: totals.fundedAmount,
    requestedAmount: totals.requestedAmount,
  };
};

// Per-country palettes for the financed distribution donut (design shades: US blues, CA greens).
const FINANCED_DISTRIBUTION_PALETTES: Record<string, string[]> = {
  US: ['#B3E5FC', '#4FC3F7', '#0288D1', '#1E88E5', '#2962FF', '#82B1FF'],
  CA: ['#DCEDC8', '#AED581', '#689F38', '#C8E6C9', '#7CB342', '#33691E'],
};

const FINANCED_DISTRIBUTION_FALLBACK = ['#90CAF9', '#64B5F6', '#42A5F5', '#1E88E5'];

// Bar mode uses one flat color per country (design: US light blue, CA light green).
const FINANCED_DISTRIBUTION_BAR_COLORS: Record<string, string> = { US: '#BBDEFB', CA: '#C8E6C9' };

export const getFinancedBarColor = (country: string): string =>
  FINANCED_DISTRIBUTION_BAR_COLORS[normalizeCountry(country)] ?? '#90CAF9';

export const mapFinancedDistribution = (rows: LenderRetailActivityLenderRowViewModel[]): FinancedDistributionDatum[] =>
  groupLenderRowsByCountry(rows).flatMap(group => {
    const palette = FINANCED_DISTRIBUTION_PALETTES[normalizeCountry(group.country)] ?? FINANCED_DISTRIBUTION_FALLBACK;

    return group.rows
      .filter(row => row.fundedAmount > 0)
      .map((row, index) => ({
        id: row.id,
        label: row.lenderName,
        value: row.fundedAmount,
        color: palette[index % palette.length],
        country: group.country,
      }));
  });

// 'Other' folds in pending apps, the only decision outcome besides approved/declined the API exposes.
export const mapCreditDecisions = (rows: LenderRetailActivityCountryRowViewModel[]): CreditDecisionDatum[] =>
  rows
    .filter(row => row.creditApps > 0)
    .sort((a, b) => getCountryRank(a.country) - getCountryRank(b.country))
    .flatMap(row => {
      const palette = FINANCED_DISTRIBUTION_PALETTES[normalizeCountry(row.country)] ?? FINANCED_DISTRIBUTION_FALLBACK;

      return [
        {
          id: `${row.country}-approved`,
          label: 'Approved',
          value: row.approvedApps,
          color: palette[0],
          country: row.country,
        },
        {
          id: `${row.country}-declined`,
          label: 'Declined',
          value: row.declinedApps,
          color: palette[1],
          country: row.country,
        },
        { id: `${row.country}-other`, label: 'Other', value: row.pendingApps, color: palette[2], country: row.country },
      ].filter(datum => datum.value > 0);
    });

// Choropleth bucket thresholds/colors for the Geographic breakdown map (matches the Figma legend).
export const CREDIT_APPS_BUCKET_LEGEND: { label: string; color: string }[] = [
  { label: '1-3 apps', color: '#DCEDC8' },
  { label: '4-6 apps', color: '#AED581' },
  { label: '7+ apps', color: '#689F38' },
];

export const getCreditAppsBucketColor = (creditApps: number): string | undefined => {
  if (creditApps <= 0) return undefined;
  if (creditApps <= 3) return CREDIT_APPS_BUCKET_LEGEND[0].color;
  if (creditApps <= 6) return CREDIT_APPS_BUCKET_LEGEND[1].color;
  return CREDIT_APPS_BUCKET_LEGEND[2].color;
};

// US state (USPS) and Canada province codes never collide, so a single flat map works for both.
export const mapCreditAppsByStateCode = (rows: LenderRetailActivityStateRowViewModel[]): Record<string, number> =>
  rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.state.trim().toUpperCase()] = row.creditApps;
    return acc;
  }, {});

export const mapDealerActivityRows = (rows: LenderRetailActivityDealerRowViewModel[]): DealerActivityRow[] =>
  rows.map((row, index) => ({
    id: row.dealerId ?? `${row.dealerName}-${index}`,
    country: row.country,
    dealerName: row.dealerName,
    creditApps: row.creditApps,
    units: row.units,
  }));

export const groupDealerActivityRowsByCountry = (
  rows: LenderRetailActivityDealerRowViewModel[]
): DealerActivityGroup[] => {
  const groups = new Map<string, DealerActivityRow[]>();

  mapDealerActivityRows(rows).forEach(row => {
    const list = groups.get(row.country) ?? [];
    list.push(row);
    groups.set(row.country, list);
  });

  return Array.from(groups.entries())
    .map(([country, countryRows]) => ({ country, rows: countryRows }))
    .sort((a, b) => getCountryRank(a.country) - getCountryRank(b.country));
};

export const getDealerActivityTotalRow = (totalCreditApps: number, totalUnits: number): DealerActivityRow => ({
  id: DEALER_ACTIVITY_TOTAL_ID,
  country: '',
  dealerName: 'Total',
  creditApps: totalCreditApps,
  units: totalUnits,
});

export const mapDeclineReasonDistribution = (
  rows: LenderRetailActivityDeclineReasonRowViewModel[]
): DeclineReasonDatum[] => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return rows
    .filter(row => row.count > 0)
    .map((row, index) => ({
      id: `${row.label}-${index}`,
      label: row.label,
      value: row.count,
      percentage: getRateOfTotal(row.count, total),
    }))
    .sort((a, b) => b.value - a.value);
};

export const LENDER_APPS_COLUMNS: LenderAppsColumnConfig[] = [
  { key: 'lenderName', header: 'Lender', align: 'left', render: row => row.lenderName },
  { key: 'approved', header: 'Approved', align: 'right', render: row => formatCount(row.approved) },
  { key: 'declined', header: 'Declined', align: 'right', render: row => formatCount(row.declined) },
  {
    key: 'pending',
    header: 'Pending',
    align: 'right',
    render: row => (row.pending > 0 ? formatCount(row.pending) : '—'),
  },
  {
    key: 'approvedAmount',
    header: 'Approval amount ($)',
    align: 'right',
    render: row => formatAmountValue(row.approvedAmount),
  },
  { key: 'approvalRate', header: 'Approval Rate', align: 'right', render: row => formatRateWhole(row.approvalRate) },
  { key: 'funded', header: 'Funded', align: 'right', render: row => formatCount(row.funded) },
  {
    key: 'fundedAmount',
    header: 'Funded amount ($)',
    align: 'right',
    render: row => formatAmountValue(row.fundedAmount),
  },
];

export const DEALER_ACTIVITY_COLUMNS: DealerActivityColumnConfig[] = [
  { key: 'dealerName', header: 'Dealer', align: 'left', render: row => row.dealerName },
  { key: 'creditApps', header: 'Apps', align: 'right', render: row => formatCount(row.creditApps) },
  { key: 'units', header: '# of Units', align: 'right', render: row => formatCount(row.units) },
];

const downloadCsv = (csv: string, fileName: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportLenderAppsToCsv = (rows: LenderRetailActivityLenderRowViewModel[]): void => {
  const groups = groupLenderRowsByCountry(rows);
  const totalRow = getLenderAppsTotalRow(rows);
  const headers = LENDER_APPS_COLUMNS.map(col => col.header);

  const csvRows = groups.flatMap(group => [
    `"${getCountryLabel(group.country)}"`,
    ...group.rows.map(row => LENDER_APPS_COLUMNS.map(col => `"${col.render(row)}"`).join(',')),
  ]);

  if (totalRow) csvRows.push(LENDER_APPS_COLUMNS.map(col => `"${col.render(totalRow)}"`).join(','));

  downloadCsv([headers.join(','), ...csvRows].join('\n'), 'lender-apps.csv');
};

export const exportDealerActivityToCsv = (
  rows: LenderRetailActivityDealerRowViewModel[],
  totalCreditApps: number,
  totalUnits: number
): void => {
  const groups = groupDealerActivityRowsByCountry(rows);
  const totalRow = getDealerActivityTotalRow(totalCreditApps, totalUnits);
  const headers = DEALER_ACTIVITY_COLUMNS.map(col => col.header);

  const csvRows = groups.flatMap(group => [
    `"${getCountryLabel(group.country)}"`,
    ...group.rows.map(row => DEALER_ACTIVITY_COLUMNS.map(col => `"${col.render(row)}"`).join(',')),
  ]);

  if (groups.length) csvRows.push(DEALER_ACTIVITY_COLUMNS.map(col => `"${col.render(totalRow)}"`).join(','));

  downloadCsv([headers.join(','), ...csvRows].join('\n'), 'dealer-activity.csv');
};
